// Client-side merge policy for private AI-action metadata.
//
// Channel broadcasts serialize answers with viewer: nil, so non-public action
// requests arrive with their prompt-bearing fields stripped and marked
// prompt_stripped (see Answer.answer_json_with_private_ai_action_metadata). That
// stripped copy reaches every subscriber — including the answer owner, whose
// local cache holds the full metadata. These merges keep the richer local fields
// when a stripped copy lands, while still taking every fresh incoming field
// (status, ai_run linkage, and the metadata_public share flag itself).
//
// Kept free of state/cache imports so the policy stays unit-testable.

const STRIPPED_PROMPT_FIELDS = ['prompt', 'prompt_placement', 'prompt_is_default'] as const
const STRIPPED_PRIVATE_SETTING_FIELDS = [
    'lyrics',
    'negative_prompt',
    'negativePrompt',
    'next_text',
    'nextText',
    'pay',
    'previous_text',
    'previousText',
] as const

type UnknownRecord = Record<string, unknown>

const asRecord = (value: unknown): UnknownRecord | null =>
    value && typeof value === 'object' && !Array.isArray(value) ? (value as UnknownRecord) : null

const hasArrayValues = (value: unknown): value is unknown[] =>
    Array.isArray(value) && value.length > 0

const cloneMetadataValue = <T>(value: T): T => {
    if (Array.isArray(value)) {
        return value.map(entry =>
            entry && typeof entry === 'object' && !Array.isArray(entry) ? { ...entry } : entry,
        ) as T
    }
    if (value && typeof value === 'object') {
        return { ...(value as UnknownRecord) } as T
    }
    return value
}

export const actionRequestIsPromptStripped = (action: unknown): boolean =>
    asRecord(action)?.prompt_stripped === true

const answerOwnerId = (answer: unknown): unknown => {
    const entry = asRecord(answer)
    if (!entry) return null
    return entry.user_id ?? asRecord(entry.user)?.id ?? null
}

export const shouldPreservePrivateAiActionMetadata = (
    incomingAnswer: unknown,
    existingAnswer: unknown,
    viewerId: unknown,
): boolean => {
    if (viewerId == null || viewerId === '') return false
    const ownerId = answerOwnerId(existingAnswer) ?? answerOwnerId(incomingAnswer)
    return ownerId != null && ownerId !== '' && String(ownerId) === String(viewerId)
}

const actionRequestIdentityKeys = (action: unknown, fallbackIndex: number): string[] => {
    const entry = asRecord(action)
    if (!entry) return [`index:${fallbackIndex}`]

    const keys = [
        entry.id != null ? `id:${String(entry.id)}` : null,
        entry.client_id != null ? `id:${String(entry.client_id)}` : null,
        entry.ai_action_id != null ? `id:${String(entry.ai_action_id)}` : null,
        `index:${fallbackIndex}`,
    ]

    return keys.filter((key): key is string => Boolean(key))
}

const aiRunIdentityKeys = (run: unknown, fallbackIndex: number): string[] => {
    const entry = asRecord(run)
    if (!entry) return [`index:${fallbackIndex}`]

    const keys = [
        entry.id != null ? `id:${String(entry.id)}` : null,
        entry.ai_run_id != null ? `id:${String(entry.ai_run_id)}` : null,
        entry.aiRunId != null ? `id:${String(entry.aiRunId)}` : null,
        `generation:${String(entry.action_index ?? entry.actionIndex ?? 0)}:${String(
            entry.generation_index ?? entry.generationIndex ?? 0,
        )}`,
        `index:${fallbackIndex}`,
    ]

    return keys.filter((key): key is string => Boolean(key))
}

export const restoreStrippedActionRequestFields = <T>(
    incomingAction: T,
    existingAction: unknown,
): T => {
    const incomingEntry = asRecord(incomingAction)
    const existingEntry = asRecord(existingAction)
    if (!incomingEntry || !existingEntry) return incomingAction

    const merged: UnknownRecord = { ...incomingEntry }

    STRIPPED_PROMPT_FIELDS.forEach(key => {
        if (merged[key] === undefined && existingEntry[key] !== undefined) {
            merged[key] = cloneMetadataValue(existingEntry[key])
        }
    })

    const existingInputs = asRecord(existingEntry.inputs)
    if (existingInputs && existingInputs.text !== undefined) {
        const incomingInputs = asRecord(merged.inputs) || {}
        if (incomingInputs.text === undefined) {
            merged.inputs = { ...incomingInputs, text: cloneMetadataValue(existingInputs.text) }
        }
    }

    const existingSettings = asRecord(existingEntry.settings)
    if (existingSettings) {
        const incomingSettings = asRecord(merged.settings) || {}
        const restoredSettings = { ...incomingSettings }
        let settingsChanged = false
        STRIPPED_PRIVATE_SETTING_FIELDS.forEach(key => {
            if (restoredSettings[key] === undefined && existingSettings[key] !== undefined) {
                restoredSettings[key] = cloneMetadataValue(existingSettings[key])
                settingsChanged = true
            }
        })
        if (settingsChanged) merged.settings = restoredSettings
    }

    delete merged.prompt_stripped
    return merged as T
}

export const mergeActionRequestsPreservingPrivateMetadata = <T>(
    incomingActions: T[] | unknown,
    existingActions: unknown,
    preservePrivateMetadata: boolean = false,
): T[] => {
    if (!Array.isArray(incomingActions)) return []
    if (!preservePrivateMetadata) return incomingActions as T[]
    if (!hasArrayValues(existingActions)) return incomingActions as T[]

    const existingByKey = new Map<string, unknown>()
    existingActions.forEach((action, index) => {
        if (actionRequestIsPromptStripped(action)) return
        actionRequestIdentityKeys(action, index).forEach(key => {
            if (!existingByKey.has(key)) existingByKey.set(key, action)
        })
    })
    if (existingByKey.size === 0) return incomingActions as T[]

    let changed = false
    const mergedActions = (incomingActions as T[]).map((action, index) => {
        if (!actionRequestIsPromptStripped(action)) return action

        const existingAction = actionRequestIdentityKeys(action, index)
            .map(key => existingByKey.get(key))
            .find(Boolean)
        if (!existingAction) return action

        changed = true
        return restoreStrippedActionRequestFields(action, existingAction)
    })

    return changed ? mergedActions : (incomingActions as T[])
}

const aiRunActionPayload = (run: unknown): UnknownRecord | null => {
    const entry = asRecord(run)
    if (!entry) return null
    return asRecord(entry.action_payload) || asRecord(entry.actionPayload)
}

export const mergeAiRunsPreservingPrivatePayloads = <T>(
    incomingRuns: T[] | unknown,
    existingRuns: unknown,
    preservePrivateMetadata: boolean = false,
): T[] => {
    if (!Array.isArray(incomingRuns)) return []
    if (!preservePrivateMetadata) return incomingRuns as T[]
    if (!hasArrayValues(existingRuns)) return incomingRuns as T[]

    const existingByKey = new Map<string, unknown>()
    existingRuns.forEach((run, index) => {
        const payload = aiRunActionPayload(run)
        if (!payload || actionRequestIsPromptStripped(payload)) return
        aiRunIdentityKeys(run, index).forEach(key => {
            if (!existingByKey.has(key)) existingByKey.set(key, run)
        })
    })
    if (existingByKey.size === 0) return incomingRuns as T[]

    let changed = false
    const mergedRuns = (incomingRuns as T[]).map((run, index) => {
        const incomingPayload = aiRunActionPayload(run)
        if (!incomingPayload || !actionRequestIsPromptStripped(incomingPayload)) return run

        const existingRun = aiRunIdentityKeys(run, index)
            .map(key => existingByKey.get(key))
            .find(Boolean)
        const existingPayload = aiRunActionPayload(existingRun)
        if (!existingPayload) return run

        const entry = run as UnknownRecord
        const existingEntry = existingRun as UnknownRecord
        const merged: UnknownRecord = {
            ...entry,
            action_payload: restoreStrippedActionRequestFields(incomingPayload, existingPayload),
        }
        if ('actionPayload' in merged) delete merged.actionPayload

        // Stripped runs keep only result_payload.outputs and a blank error_payload;
        // restore the richer local payloads while taking any fresher outputs.
        const existingResult = asRecord(existingEntry.result_payload)
        if (existingResult && Object.keys(existingResult).some(key => key !== 'outputs')) {
            const incomingOutputs = asRecord(entry.result_payload)?.outputs
            merged.result_payload = {
                ...cloneMetadataValue(existingResult),
                ...(incomingOutputs !== undefined
                    ? { outputs: cloneMetadataValue(incomingOutputs) }
                    : {}),
            }
        }

        const incomingError = asRecord(entry.error_payload)
        const existingError = asRecord(existingEntry.error_payload)
        if (
            (!incomingError || Object.keys(incomingError).length === 0) &&
            existingError &&
            Object.keys(existingError).length > 0
        ) {
            merged.error_payload = cloneMetadataValue(existingError)
        }

        changed = true
        return merged as T
    })

    return changed ? mergedRuns : (incomingRuns as T[])
}
